// @flow
import React from 'react';
import { ToastContainer, toast } from 'react-toastify';
import _ from 'lodash';
import type { Message } from '../../models/group_chats/Message';
import { link, content } from '../../models/group_chats/Message';
import NotificationClient from '../../models/group_chats/NotificationClient';
import NotificationItem from './NotificationItem';

type Props = {
  memberId: number,
  memberType: string,
  userId: number,
  className: string,
};

type State = {
  notifications: Array<Message>,
  meta: any,
  hasNewNotifications: boolean,
  isShowNotification: boolean,
  groupChatMembers: Array<any>,
  newNotification: ?Message,
  isLoadMore: boolean,
};

export default class NotificationView extends React.PureComponent<
  Props,
  State,
> {
  notificationClient: NotificationClient;
  listNotificationsNode: any;
  currentNotification: any;
  wrapperRef: any;

  static DEFAULT_META = {
    current_page: 1,
    total_pages: 1,
    total_count: 1,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      notifications: [],
      meta: NotificationView.DEFAULT_META,
      hasNewNotifications: false,
      newNotification: null,
      groupChatMembers: [],
      isShowNotification: false,
      isLoadMore: false,
    };
    const { userId } = props;
    this.notificationClient = new NotificationClient(userId);
    this.notificationClient.received = this.handleReceivedNotifications;
  }

  onUnload() {
    if (this.currentNotification) {
      this.currentNotification.close();
    }
  }

  async componentDidMount() {
    window.addEventListener('beforeunload', () => this.onUnload());
    document.addEventListener('mousedown', (event: any) =>
      this.handleClickOutsideNotificationList(event),
    );
    const response = await this.notificationClient.checkHaveNotificationUnread();
    this.setState({
      hasNewNotifications: response.data,
    });
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', () => this.onUnload());
    document.removeEventListener('mousedown', (event: any) =>
      this.handleClickOutsideNotificationList(event),
    );
  }

  openNotifications() {
    const { isShowNotification } = this.state;

    this.setState(
      {
        isShowNotification: !isShowNotification,
        isLoadMore: true,
      },
      () => {
        if (!isShowNotification) {
          this.notificationClient.fetchNotification(1).then(res => {
            this.setState({
              hasNewNotifications: false,
              meta: res.data.meta,
              notifications: res.data.notifications,
              groupChatMembers: res.data.group_chat_members,
              isLoadMore: false,
            });
          });
        }
      },
    );
  }

  handleClickOutsideNotificationList(event: any) {
    if (this.wrapperRef && !this.wrapperRef.contains(event.target)) {
      this.setState({
        isShowNotification: false,
      });
    }
  }

  notifyNotification = async (
    contentNotification: string,
    linkTo: string,
    currentLocation: string,
  ) => {
    const focused = document.hasFocus();
    if (!focused || currentLocation.indexOf(linkTo) === -1) {
      let notification = new Notification(contentNotification);
      if (this.currentNotification) this.currentNotification.close();
      this.currentNotification = notification;
      notification.onclick = await function(event) {
        event.preventDefault();
        if (window.location.pathname !== linkTo) window.location.href = linkTo;
        window.focus();
        this.close();
      };
      setTimeout(notification.close.bind(notification), 5000);
    }
  };

  handleReceivedNotifications = (data: any) => {
    const { memberType, memberId } = this.props;
    const currentLocation = window.location.href;
    for (let i = 0; i < data.receivers.length; i++) {
      if (
        data.receivers[i][0] === memberId &&
        data.receivers[i][1] === memberType
      ) {
        const linkTo = link(data.message, memberType);
        this.setState({
          hasNewNotifications: currentLocation.indexOf(linkTo) === -1,
          newNotification: data.message,
        });
        const contentNotification = content(data.message, memberType, memberId);
        if (currentLocation.indexOf(linkTo) === -1) {
          toast(
            <NotificationItem
              notification={data.message}
              memberType={memberType}
              memberId={memberId}
              content={contentNotification}
              link={linkTo}
              isUnread={false}
            />,
          );
        }
        this.notifyNotification(contentNotification, linkTo, currentLocation);
      }
    }
  };

  handleScroll = async () => {
    const isAtBottom =
      this.listNotificationsNode.clientHeight +
        this.listNotificationsNode.scrollTop <
      this.listNotificationsNode.scrollHeight;
    if (isAtBottom || this.state.isLoadMore || !this.state.meta.next_page)
      return;
    this.setState({ isLoadMore: true });
    try {
      const response = await this.notificationClient.fetchNotification(
        this.state.meta.next_page,
      );
      this.setState(oldState => ({
        notifications: [
          ...oldState.notifications,
          ...response.data.notifications,
        ],
        meta: response.data.meta,
      }));
    } catch (error) {
      console.warn(error);
    } finally {
      this.setState({ isLoadMore: false });
    }
  };

  isUnread = (groupChatMembers: Array<any>, notification: any) => {
    const { memberType, memberId } = this.props;
    const member = groupChatMembers.find(a => {
      return (
        a.memberType == memberType &&
        a.memberId == memberId &&
        a.groupChatId == notification.groupChatId
      );
    });
    return !member || notification.updatedAt > member.lastRead;
  };

  render() {
    const {
      hasNewNotifications,
      isLoadMore,
      isShowNotification,
      notifications,
      groupChatMembers,
    } = this.state;
    const { memberType, memberId, className } = this.props;
    return (
      <li
        ref={ref => (this.wrapperRef = ref)}
        className={`nav-item notification ${
          isShowNotification ? 'notification__active' : ''
        }`}
      >
        <a
          className={`nav-link notification__icon ${
            hasNewNotifications ? 'notification__pointer' : ''
          }`}
          onClick={() => this.openNotifications()}
        >
          <i className={`${className}`} />
        </a>
        {isShowNotification && (
          <span className="notification__active__title">お知らせ</span>
        )}
        <ul
          className={`notification__messages ${
            isShowNotification ? 'notification__messages--active' : ''
          }`}
          onScroll={this.handleScroll}
          ref={e => {
            this.listNotificationsNode = e;
          }}
        >
          {notifications.map((notification, index) => {
            return (
              <NotificationItem
                notification={notification}
                memberType={memberType}
                memberId={memberId}
                content={content(notification, memberType, memberId)}
                link={link(notification, memberType)}
                isUnread={this.isUnread(groupChatMembers, notification)}
                key={index}
              />
            );
          })}
          {isLoadMore && (
            <li className="notification__empty">
              <span className="notification__loading" />
            </li>
          )}
          {!isLoadMore && _.isEmpty(notifications) && (
            <li className="notification__empty">
              <span>通知はありません</span>
            </li>
          )}
        </ul>
        <ToastContainer hideProgressBar={true} autoClose={5000} />
      </li>
    );
  }
}
